// components/(postTypes)/symbols/single/coin/layout/SingleCoinHeader.tsx
import React, { useEffect, useState } from 'react';
import SingleCoinTitle from '@/components/(postTypes)/symbols/single/coin/layout/SingleCoinTitle';
import { CoinProps } from '@/types/coins';
import SinglePrice from '@/components/(postTypes)/symbols/single/coin/layout/SinglePrice';

interface SingleCoinHeaderProps extends CoinProps {
    slug: string;
    timePeriod: string;
    updateTimePeriod: (newTimePeriod: string) => void;
    change: number;
    /** 24h change in toman (gold & currency) */
    changeToman?: number;
    isCurrencyOrGold?: boolean;
    /** Rendered inside the content column (gap handled by the column) */
    inColumn?: boolean;
    symbolType?: 'crypto' | 'coin' | 'gold' | 'currency';
    /** «به‌روزرسانی ۱۳ آبان ۱۴۰۵، ساعت ۱۷:۳۴», formatted on the server (see the symbol page) */
    updatedAt?: { iso: string; label: string };
}

type TimePeriod = '1h' | '24h' | '7d' | '30d' | '1y' | '5y';

const SingleCoinHeader: React.FC<SingleCoinHeaderProps> = ({
    timePeriod,
    nameFa,
    typeId,
    asset_type,
    slug,
    symbol,
    rank,
    price,
    name,
    iconUrl,
    change,
    changeToman,
    isCurrencyOrGold = false,
    inColumn = false,
    symbolType = 'crypto',
    price_usd,
    quote_currency,
    updatedAt,
}) => {
    const timePeriodTranslations: Record<TimePeriod, string> = {
        '1h': 'ساعتی',
        '24h': 'روزانه',
        '7d': 'هفتگی',
        '30d': 'ماهانه',
        '1y': 'سالانه',
        '5y': '۵ سال',
    };

    const isUSDT = symbol === 'USDT';

    const [isScrolledUp, setIsScrolledUp] = useState(false);
    const [lastScrollTop, setLastScrollTop] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

            if (scrollTop < lastScrollTop) {
                setIsScrolledUp(true);
            } else {
                setIsScrolledUp(false);
            }
            setLastScrollTop(scrollTop <= 0 ? 0 : scrollTop);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScrollTop]);

    // Desktop: sticky and opaque; the `before:` strip fills the gap up to the site menu, so content
    // scrolling under it (the chart) never shows above or through the header.
    return (
        <div
            className={`lg:sticky z-30 lg:bg-white lg:dark:bg-primary-active lg:before:pointer-events-none lg:before:absolute lg:before:-inset-x-px lg:before:bottom-full lg:before:h-5 lg:before:bg-gray-50 lg:dark:before:bg-primary-dark lg:before:content-[''] lg:border lg:border-gray-100 lg:dark:border-white/10 lg:shadow-sm lg:rounded-2xl p-3 sm:p-4 mb-1 sm:mb-6 ${inColumn ? 'lg:mb-0' : ''} flex flex-row items-center justify-between gap-3 transition-all ease-in-out duration-500 ${
                isScrolledUp ? 'lg:top-[124px]' : 'lg:top-[60px]'
            }`}
        >
            <SingleCoinTitle
                rank={rank || 0}
                symbol={symbol || ''}
                nameFa={nameFa}
                iconUrl={iconUrl || ''}
                name={name || ''}
                currentSlug={slug}
                symbolType={symbolType}
            />

            <div className='flex flex-col items-end gap-1 min-w-0'>
                <SinglePrice
                    symbol={symbol || ''}
                    isUSDT={isUSDT}
                    price_buy={typeof price === 'object' ? price?.price_buy || 0 : 0}
                    ir_price_buy={
                        typeof price === 'number'
                            ? price
                            : typeof price === 'object'
                              ? price?.ir_price_buy || 0
                              : 0
                    }
                    typeId={typeId}
                    asset_type={asset_type}
                    timePeriod={timePeriod}
                    timePeriodTranslations={timePeriodTranslations}
                    change={change || 0}
                    changeToman={changeToman}
                    isCurrencyOrGold={isCurrencyOrGold}
                    symbolType={symbolType}
                    priceUsd={price_usd}
                    quoteCurrency={quote_currency}
                />
                {updatedAt && (
                    <time
                        dateTime={updatedAt.iso}
                        className='text-[10px] sm:text-[11px] leading-4 text-gray-500 dark:text-gray-400 whitespace-nowrap'
                    >
                        {updatedAt.label}
                    </time>
                )}
            </div>
        </div>
    );
};

export default SingleCoinHeader;
